import { useEffect, useState, useCallback } from "react";
import { Loader2, Handshake, ExternalLink, Plus, ShieldCheck, ShieldAlert, Users, CheckCircle2, TrendingUp, AlertTriangle, Eye, MousePointerClick, UserPlus, DollarSign, BadgeCheck, Ban, Sparkles, Star } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { OVERLINE, H1, Panel, btnPrimary, StatusBadge, useAdmin } from "@/components/admin/ui";

function OverviewCards({ m, traffic }) {
  const cards = [
    { icon: Users, label: "Total partners", value: m.total, sub: `${m.published} published · ${m.draft} draft` },
    { icon: CheckCircle2, label: "Verified", value: m.by_verification.verified, sub: `${m.by_verification.pending} pending review` },
    { icon: ShieldCheck, label: "Active subscriptions", value: m.subscribed },
    { icon: Eye, label: "Total profile views", value: traffic.views },
    { icon: MousePointerClick, label: "Referral clicks", value: traffic.referral_clicks, sub: `${traffic.signups} signups` },
    { icon: DollarSign, label: "Attributed value", value: `$${(traffic.conversion_value / 100).toLocaleString()}`, sub: `${traffic.conversions} conversions` },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="overview-cards">
      {cards.map((c) => (
        <div key={c.label} className="border border-[#E5E2DC] bg-white p-4" data-testid={`overview-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
          <c.icon size={15} className="text-[#8A857C]" />
          <p className="font-serif text-2xl text-[#1F2A22] mt-2 tabular-nums">{typeof c.value === "number" ? c.value.toLocaleString() : c.value}</p>
          <p className="text-[11px] text-[#5C6B61] mt-0.5">{c.label}</p>
          {c.sub && <p className="text-[10px] text-[#8A857C] mt-0.5">{c.sub}</p>}
        </div>
      ))}
    </div>
  );
}

function ApprovalsQueue({ items, onAction }) {
  const [busy, setBusy] = useState(null);
  const act = async (id, status) => {
    setBusy(id + status);
    try { await api.verifyBusiness(id, status); toast.success(status.startsWith("verified") ? "Partner verified" : status === "suspended" ? "Partner suspended" : "Set to unverified"); onAction(); }
    catch { toast.error("Could not update"); }
    setBusy(null);
  };
  return (
    <Panel title={`Approvals queue (${items.length})`} data-testid="approvals-panel">
      <p className="text-sm text-[#5C6B61] mb-3">Published partners awaiting a verification badge. Verifying grants the official badge on their public profile.</p>
      {items.length === 0 ? <p className="text-sm text-[#5C6B61]">No partners awaiting review. 🎉</p> : (
        <div className="space-y-2" data-testid="approvals-list">
          {items.map((p) => (
            <div key={p.id} className="flex items-center gap-3 border border-[#E5E2DC] bg-white p-3 flex-wrap" data-testid={`approval-${p.id}`}>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-[#1F2A22] truncate">{p.name} <span className="text-[10px] font-bold uppercase text-[#8A857C]">{p.partner_type}</span></p>
                <p className="text-xs text-[#5C6B61]">{p.completion}% complete{p.public_path && <> · <a href={p.public_path} target="_blank" rel="noreferrer" className="text-[#FF6A00] font-semibold">preview</a></>}</p>
              </div>
              <button onClick={() => act(p.id, p.suggested_status)} disabled={busy} className="text-xs font-semibold px-3 py-1.5 bg-[#1F2A22] text-[#F9F8F6] inline-flex items-center gap-1 disabled:opacity-50" data-testid={`approve-${p.id}`}>
                {busy === p.id + p.suggested_status ? <Loader2 size={12} className="animate-spin" /> : <BadgeCheck size={12} />} Verify
              </button>
              <button onClick={() => act(p.id, "unverified")} disabled={busy} className="text-xs font-semibold px-3 py-1.5 border border-[#E5E2DC] text-[#5C6B61]" data-testid={`reject-${p.id}`}>Reject</button>
              <button onClick={() => act(p.id, "suspended")} disabled={busy} className="text-xs font-semibold px-3 py-1.5 border border-red-300 text-red-700 inline-flex items-center gap-1" data-testid={`suspend-${p.id}`}><Ban size={12} /> Suspend</button>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

function HealthPanel({ health }) {
  const groups = [
    { key: "subscribed_not_published", label: "Paid but not published", icon: AlertTriangle, tone: "text-amber-700", hint: "Active subscription but hasn't published their profile." },
    { key: "low_completion", label: "Low profile completion", icon: ShieldAlert, tone: "text-amber-700", hint: "Published but under 80% complete." },
    { key: "inactive_subscription", label: "Inactive subscription", icon: Ban, tone: "text-red-700", hint: "Partner without an active subscription." },
  ];
  return (
    <Panel title="Partner health" data-testid="health-panel">
      <div className="space-y-4">
        {groups.map((g) => {
          const list = health[g.key] || [];
          return (
            <div key={g.key} data-testid={`health-${g.key}`}>
              <div className="flex items-center gap-2">
                <g.icon size={15} className={g.tone} />
                <span className="text-sm font-semibold text-[#1F2A22]">{g.label}</span>
                <span className={`text-xs font-bold ${list.length ? g.tone : "text-[#8A857C]"}`}>{list.length}</span>
              </div>
              <p className="text-[11px] text-[#8A857C] ml-6">{g.hint}</p>
              {list.length > 0 && (
                <div className="ml-6 mt-1 flex flex-wrap gap-1.5">
                  {list.slice(0, 8).map((p) => <span key={p.id} className="text-xs bg-[#F3EFE6] px-2 py-0.5 text-[#5C6B61]">{p.name}{g.key === "low_completion" ? ` (${p.completion}%)` : ""}</span>)}
                </div>
              )}
            </div>
          );
        })}
        <div className="flex items-center gap-2 pt-1 border-t border-[#E5E2DC]" data-testid="health-updates">
          <TrendingUp size={15} className="text-[#8A857C]" />
          <span className="text-sm text-[#5C6B61]">{health.total_updates} partner updates · {health.hidden_updates} hidden by admin</span>
        </div>
      </div>
    </Panel>
  );
}

function TopPartners({ items }) {
  const max = Math.max(1, ...items.map((p) => p.views));
  return (
    <Panel title="Top partners by traffic" data-testid="top-partners-panel">
      {items.length === 0 ? <p className="text-sm text-[#5C6B61]">No traffic yet.</p> : (
        <div className="space-y-2.5">
          {items.map((p) => (
            <div key={p.id} data-testid={`top-partner-${p.id}`}>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-[#1F2A22] font-medium truncate">{p.name} <span className="text-[10px] uppercase text-[#8A857C]">{p.partner_type}</span></span>
                <span className="font-semibold text-[#1F2A22] tabular-nums">{p.views.toLocaleString()} views</span>
              </div>
              <div className="h-2 bg-[#F3EFE6] overflow-hidden"><div className="h-full bg-[#1F2A22]" style={{ width: `${(p.views / max) * 100}%` }} /></div>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

const TYPES = [
  { id: "brand", label: "Brand" }, { id: "outfitter", label: "Outfitter" },
  { id: "guide", label: "Guide" }, { id: "state", label: "State Partner" },
];

function ProvisionForm({ onDone }) {
  const [form, setForm] = useState({ email: "", partner_type: "state", name: "", subscription_status: "complimentary", plan: "elite" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async () => {
    if (!form.email.trim()) { toast.error("User email is required"); return; }
    setBusy(true);
    try {
      const res = await api.adminProvisionPartner({ ...form, email: form.email.trim().toLowerCase() });
      toast.success(`Provisioned ${res.partner?.name || "partner"} — onboarding ready`);
      setForm({ email: "", partner_type: "state", name: "", subscription_status: "complimentary", plan: "elite" });
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not provision"); }
    setBusy(false);
  };
  return (
    <Panel title="Provision a partner (complimentary / state / custom)" data-testid="provision-panel">
      <p className="text-sm text-[#5C6B61] mb-4">Grant an existing HomeRange user partner access without Stripe checkout. They then complete the same onboarding flow. The user must already have a HomeRange account.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <Input label="User email" v={form.email} on={set("email")} testid="provision-email" />
        <Input label="Partner name (optional)" v={form.name} on={set("name")} testid="provision-name" />
        <Select label="Partner type" v={form.partner_type} on={set("partner_type")} testid="provision-type" opts={TYPES.map((t) => [t.id, t.label])} />
        <Select label="Access" v={form.subscription_status} on={set("subscription_status")} testid="provision-status"
          opts={[["complimentary", "Complimentary"], ["state_contract", "State contract"], ["active", "Active (custom)"], ["trialing", "Trialing"]]} />
      </div>
      <button onClick={submit} disabled={busy} className={`${btnPrimary} mt-4`} data-testid="provision-submit">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <><Plus size={15} /> Provision partner</>}
      </button>
    </Panel>
  );
}

function Input({ label, v, on, testid }) {
  return (
    <div>
      <label className="text-[11px] font-bold uppercase tracking-wide text-[#5C6B61]">{label}</label>
      <input value={v} onChange={on} className="w-full mt-1 border border-[#E5E2DC] bg-white px-3 py-2 text-sm text-[#1F2A22]" data-testid={testid} />
    </div>
  );
}
function Select({ label, v, on, testid, opts }) {
  return (
    <div>
      <label className="text-[11px] font-bold uppercase tracking-wide text-[#5C6B61]">{label}</label>
      <select value={v} onChange={on} className="w-full mt-1 border border-[#E5E2DC] bg-white px-3 py-2 text-sm text-[#1F2A22]" data-testid={testid}>
        {opts.map(([val, lab]) => <option key={val} value={val}>{lab}</option>)}
      </select>
    </div>
  );
}

function ModerationPanel() {
  const [updates, setUpdates] = useState(null);
  const load = useCallback(() => { api.adminPartnerUpdates({ limit: 30 }).then((d) => setUpdates(d.updates)).catch(() => setUpdates([])); }, []);
  useEffect(() => { load(); }, [load]);
  const toggle = async (u) => {
    try { await api.adminModeratePartnerUpdate(u.id, !u.hidden); toast.success(u.hidden ? "Update restored" : "Update hidden"); load(); }
    catch { toast.error("Could not update"); }
  };
  return (
    <Panel title="Partner updates — moderation" data-testid="moderation-panel">
      <p className="text-sm text-[#5C6B61] mb-3">Hide any partner update that violates guidelines. Hidden updates disappear from the community feed and partner profiles.</p>
      {updates === null ? <div className="py-8 grid place-items-center"><Loader2 className="animate-spin text-[#1F2A22]" /></div>
        : updates.length === 0 ? <p className="text-sm text-[#5C6B61]">No partner updates yet.</p>
        : (
          <div className="space-y-2" data-testid="moderation-list">
            {updates.map((u) => (
              <div key={u.id} className="flex items-center gap-3 border border-[#E5E2DC] bg-white p-3" data-testid={`mod-update-${u.id}`}>
                {u.media && <img src={u.media} alt="" className="w-10 h-10 object-cover shrink-0" />}
                <div className="flex-1 min-w-0">
                  <span className="text-[10px] font-bold uppercase text-[#FF6A00]">{u.partner_name}</span>
                  <p className="text-sm text-[#1F2A22] font-semibold truncate">{u.title || u.text}</p>
                </div>
                {u.hidden && <span className="text-[10px] font-bold uppercase bg-red-100 text-red-700 px-2 py-0.5">Hidden</span>}
                <button onClick={() => toggle(u)} className={`text-xs font-semibold px-3 py-1.5 border ${u.hidden ? "border-[#1F2A22] text-[#1F2A22]" : "border-red-300 text-red-700"}`} data-testid={`mod-toggle-${u.id}`}>
                  {u.hidden ? "Restore" : "Hide"}
                </button>
              </div>
            ))}
          </div>
        )}
    </Panel>
  );
}

function SystemHealthPanel() {
  const [h, setH] = useState(null);
  useEffect(() => { api.adminPartnerSystemHealth().then(setH).catch(() => setH(null)); }, []);
  const dot = { ok: "bg-emerald-500", warning: "bg-amber-500", error: "bg-red-500" };
  const overallLabel = { ok: "All systems healthy", warning: "Attention needed", error: "Action required" };
  return (
    <Panel title="System health" data-testid="system-health-panel">
      {!h ? <div className="py-6 grid place-items-center"><Loader2 className="animate-spin text-[#1F2A22]" /></div> : (
        <>
          <div className="flex items-center gap-2 mb-4" data-testid="system-health-overall">
            <span className={`w-3 h-3 rounded-full ${dot[h.overall]}`} />
            <span className="text-sm font-semibold text-[#1F2A22]">{overallLabel[h.overall]}</span>
            <span className="text-xs text-[#8A857C] ml-2">{h.summary.approved} approved · {h.summary.indexed} indexed · {h.summary.events_24h} events (24h)</span>
          </div>
          <div className="space-y-1.5">
            {h.checks.map((c) => (
              <div key={c.key} className="flex items-start gap-2.5 py-1.5 border-b border-[#F0ECE4] last:border-0" data-testid={`health-check-${c.key.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <span className={`w-2.5 h-2.5 rounded-full mt-1 shrink-0 ${dot[c.status]}`} />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#1F2A22]">{c.key}</p>
                  <p className="text-xs text-[#5C6B61]">{c.detail}{c.items.length ? ` — ${c.items.join(", ")}` : ""}</p>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </Panel>
  );
}

function AiIndexPanel() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api.adminPartnerAiIndex().then(setStatus).catch(() => setStatus(null)); }, []);
  useEffect(() => { load(); }, [load]);
  const reindex = async () => {
    setBusy(true);
    try { const r = await api.adminPartnerAiReindex(); toast.success(`Reindexed ${r.partners_indexed} partners (${r.chunks} chunks)`); load(); }
    catch { toast.error("Reindex failed"); }
    setBusy(false);
  };
  return (
    <Panel title="HomeRange AI index" data-testid="ai-index-panel">
      <p className="text-sm text-[#5C6B61] mb-3">Approved partners&apos; profiles, offerings and updates feed HomeRange AI as lowest-authority, clearly-labeled recommendations — never as a source for regulations. Content re-indexes automatically on verify, publish and update; use Reindex to rebuild manually.</p>
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2 text-sm text-[#1F2A22]"><Sparkles size={15} className="text-[#FF6A00]" /> <span data-testid="ai-index-stats"><strong>{status?.partners_indexed ?? "—"}</strong> partners · <strong>{status?.chunks ?? "—"}</strong> chunks indexed</span></div>
        <button onClick={reindex} disabled={busy} className="text-xs font-semibold px-3 py-1.5 bg-[#1F2A22] text-[#F9F8F6] inline-flex items-center gap-1 disabled:opacity-50" data-testid="ai-reindex-btn">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Reindex now
        </button>
      </div>
    </Panel>
  );
}

export default function AdminPartners() {
  const { admin } = useAdmin();
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("");
  const [overview, setOverview] = useState(null);
  const load = useCallback(() => {
    api.adminPartners(filter ? { partner_type: filter } : {}).then((d) => setRows(d.partners)).catch(() => setRows([]));
  }, [filter]);
  const loadOverview = useCallback(() => {
    api.adminPartnersOverview().then(setOverview).catch(() => setOverview(null));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadOverview(); }, [loadOverview]);
  const canManage = admin?.permissions?.includes("marketplace");
  const refreshAll = () => { load(); loadOverview(); };

  return (
    <div className="p-6 md:p-8" data-testid="admin-partners">
      <span className={OVERLINE}>Partner Platform</span>
      <h1 className={`${H1} mt-1`}>Partner Command Center</h1>
      <p className="text-sm text-[#5C6B61] mt-2">Unified overview, approvals, health and metrics across all HomeRange partners.</p>

      {overview ? (
        <>
          <div className="mt-6"><OverviewCards m={overview.metrics} traffic={overview.traffic} /></div>
          <div className="grid lg:grid-cols-2 gap-6 mt-6">
            <ApprovalsQueue items={overview.approvals} onAction={refreshAll} />
            <HealthPanel health={overview.health} />
          </div>
          <div className="mt-6"><TopPartners items={overview.top_partners} /></div>
          <div className="mt-6"><SystemHealthPanel /></div>
        </>
      ) : <div className="py-12 grid place-items-center"><Loader2 className="animate-spin text-[#1F2A22]" /></div>}

      {canManage && <div className="mt-6"><ProvisionForm onDone={refreshAll} /></div>}
      {canManage && <div className="mt-6"><AiIndexPanel /></div>}
      {canManage && <div className="mt-6"><ModerationPanel /></div>}

      <div className="mt-8 flex items-center gap-2">
        <button onClick={() => setFilter("")} className={`text-xs font-semibold px-3 py-1.5 border ${!filter ? "bg-[#1F2A22] text-[#F9F8F6] border-[#1F2A22]" : "border-[#E5E2DC] text-[#5C6B61]"}`} data-testid="filter-all">All</button>
        {TYPES.map((t) => (
          <button key={t.id} onClick={() => setFilter(t.id)} className={`text-xs font-semibold px-3 py-1.5 border ${filter === t.id ? "bg-[#1F2A22] text-[#F9F8F6] border-[#1F2A22]" : "border-[#E5E2DC] text-[#5C6B61]"}`} data-testid={`filter-${t.id}`}>{t.label}</button>
        ))}
      </div>

      {rows === null ? <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-[#1F2A22]" /></div>
        : rows.length === 0 ? <div className="mt-6 border border-dashed border-[#E5E2DC] bg-white p-16 text-center text-[#5C6B61]"><Handshake size={28} className="mx-auto mb-2 text-[#E5E2DC]" />No partners yet.</div>
        : (
          <div className="mt-4 overflow-x-auto border border-[#E5E2DC] bg-white" data-testid="partners-table">
            <table className="w-full text-sm">
              <thead className="bg-[#F3EFE6] text-left text-[11px] uppercase tracking-wide text-[#5C6B61]">
                <tr>
                  <th className="px-4 py-3">Partner</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Complete</th><th className="px-4 py-3">Views</th><th className="px-4 py-3">Scans</th>
                  <th className="px-4 py-3">Ref clicks</th><th className="px-4 py-3">Signups</th><th className="px-4 py-3">Featured</th><th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.id} className="border-t border-[#E5E2DC]" data-testid={`partner-row-${p.id}`}>
                    <td className="px-4 py-3 font-semibold text-[#1F2A22]">{p.name}</td>
                    <td className="px-4 py-3 capitalize text-[#5C6B61]">{p.partner_type || "—"}</td>
                    <td className="px-4 py-3">{p.subscription_status ? <StatusBadge status={p.subscription_status} /> : <span className="text-[#5C6B61]">inactive</span>}</td>
                    <td className="px-4 py-3 tabular-nums">{p.completion}%</td>
                    <td className="px-4 py-3 tabular-nums">{p.views}</td>
                    <td className="px-4 py-3 tabular-nums">{p.qr_scans}</td>
                    <td className="px-4 py-3 tabular-nums">{p.referral_clicks}</td>
                    <td className="px-4 py-3 tabular-nums">{p.signups}</td>
                    <td className="px-4 py-3">
                      <button onClick={async () => { try { await api.adminFeaturePartner(p.id, !p.featured); toast.success(p.featured ? "Removed from featured" : "Featured!"); refreshAll(); } catch { toast.error("Could not update"); } }}
                        aria-pressed={!!p.featured} aria-label={p.featured ? `Unfeature ${p.name}` : `Feature ${p.name} on homepage`}
                        className={p.featured ? "text-[#FF6A00]" : "text-[#C9C4BA] hover:text-[#FF6A00]"} title={p.featured ? "Unfeature" : "Feature on homepage"} data-testid={`feature-toggle-${p.id}`}>
                        <Star size={16} className={p.featured ? "fill-[#FF6A00]" : ""} />
                      </button>
                    </td>
                    <td className="px-4 py-3">{p.slug && p.partner_type && <a href={`/${{ brand: "brands", outfitter: "outfitters", guide: "guides", state: "states" }[p.partner_type]}/${p.slug}`} target="_blank" rel="noreferrer" className="text-[#FF6A00] inline-flex items-center gap-1 text-xs font-semibold" data-testid={`partner-view-${p.id}`}>View <ExternalLink size={11} /></a>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      <p className="text-sm text-[#5C6B61] mt-3">{rows?.length || 0} partners</p>
    </div>
  );
}
